'use client';

import type { ReactNode } from 'react';
import { CaretDown } from '@/components/icons';
import styles from './split-pill.module.css';

/**
 * THE SPLIT PILL: one accent control of two segments - a WORD that fires the
 * act and a CARET that opens the rest of them.
 *
 * Born in the compact dock's action pod (2026-09-16) and extracted here
 * 2026-09-21, when the desktop item head took the same control: a page's
 * primary act wears the one accent voice on the screen, and having it drawn
 * twice is how two surfaces end up disagreeing about what the next act is.
 * The rows behind the caret are the caller's `Menu`; this component owns the
 * two buttons and their geometry, nothing else.
 *
 * The WORD is optional at the `inline` size only: where nothing moves the
 * object on (a concluded item), the desktop head's caret stands alone as a
 * circle and the sheet still holds the way back. The dock never draws the
 * pill without a word (Leon, 2026-10-19: the accent belongs to a pushed act);
 * there the act sheet opens from a quiet pod instead, or the "…" holds the
 * way back alone (`item-dock.tsx`, `dockActSeat`).
 *
 * The CARET is optional too (`caret={false}`, 2026-11-03): a done or
 * cancelled item's one act is Reopen, its forward edge, and a caret opening
 * an empty sheet would be a dropdown with nothing in it.
 */
export function SplitPill({
  word,
  onWord,
  tick = false,
  quiet = false,
  wordClassName,
  wordTestId,
  caretLabel,
  caretTestId,
  caret = true,
  open,
  onToggle,
  disabled = false,
  framed = true,
  size = 'inline',
}: {
  /** The act's verb, or null when there is no one-tap act here. */
  word: string | null;
  onWord: (() => void) | null;
  /** The word is the TICK ("I am done", docs/RESOLUTION.md §2; the item
   *  page's primary act since 2026-11-03): it wears the tick's box before the
   *  verb, the same box a checklist line ticks with. */
  tick?: boolean;
  /** The QUIET register (Leon, 2026-11-03): the outline in place of the
   *  accent fill, for a word that is the way back (Reopen on a done or
   *  cancelled item), since the accent belongs to a step forward. The framed
   *  pill draws it here; an unframed host draws it on its own slot. */
  quiet?: boolean;
  /** The host's own word face (the dock's pod word). Defaults to this
   *  module's, which is what the desktop head uses. */
  wordClassName?: string;
  wordTestId?: string;
  /** The caret's accessible name - the register it opens. */
  caretLabel: string;
  caretTestId?: string;
  /** Draw the caret. False where the sheet holds nothing beside the word. */
  caret?: boolean;
  /** Whether the caller's menu is open (the caret's `aria-expanded`). */
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  /** Draw the accent frame. False where the host already draws one (the dock's
   *  pod slot owns the fill and the shape there). */
  framed?: boolean;
  /** `inline` sits on the control-height tokens (the item head's cluster);
   *  `dock` takes the pod row's thumb geometry. */
  size?: 'inline' | 'dock';
}) {
  const body: ReactNode = (
    <>
      {word && onWord ? (
        <button
          type="button"
          className={wordClassName ?? styles.word}
          data-testid={wordTestId}
          disabled={disabled}
          onClick={onWord}
          data-tick={tick || undefined}
        >
          {tick && <span className={styles.box} aria-hidden />}
          {word}
        </button>
      ) : null}
      {caret && (
        <button
          type="button"
          className={styles.more}
          data-size={size}
          data-alone={(size === 'inline' && !(word && onWord)) || undefined}
          data-testid={caretTestId}
          aria-label={caretLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={disabled}
          onClick={onToggle}
        >
          <CaretDown className={styles.caret} aria-hidden />
        </button>
      )}
    </>
  );
  return framed ? (
    <span className={styles.pill} data-quiet={quiet || undefined}>
      {body}
    </span>
  ) : (
    body
  );
}
